/**
 * Storage Adapter - Abstraction layer over persistence backends.
 *
 * Implements two adapters (v1):
 *   LocalAdapter    - synchronous writes via persistenceEngine (localStorage)
 *   SupabaseAdapter - async cloud writes to project_content table
 *
 * Interface is designed for future extension. Adapters NOT yet implemented:
 *   IndexedDBAdapter - for larger project assets
 *   S3Adapter        - for file-blob storage at scale
 *
 * NOTE: project_content table is created by Supabase migration
 * 20260527000001_project_content.sql. After running the migration, regenerate
 * types: `supabase gen types typescript --local > src/integrations/supabase/types.ts`
 */

import { persistSave, persistLoad } from "./persistenceEngine";
import { supabase } from "@/integrations/supabase/client";
import { logger }   from "@/system/logger";
import { runMigrations, payloadNeedsMigration, CURRENT_PAYLOAD_VERSION, SCOPE_PAYLOAD } from "./migration/index";
import { supabaseCircuit, CircuitOpenError } from "../runtime/httpCircuitBreaker";
import type { NuvraSchema } from "@/core/schema/nuvraSchema";

/**
 * Canonical schema version for the project content format.
 * v1 → v2 (Phase 2): added canonicalSchema optional field.
 */
export const CURRENT_SCHEMA_VERSION = 2;

/** Full project content payload - written to both local and cloud adapters. */
export interface ProjectContentPayload {
  projectId:     string;
  userId:        string;
  pages:         unknown[];
  blocks:        unknown[];
  schema:        unknown[];
  workflows:     string[];
  designSystem:  Record<string, string>;
  settings:      Record<string, unknown>;
  files:         unknown[];
  sequenceNum:   number;
  schemaVersion: number;
  savedAt:       number; // epoch ms
  /**
   * Canonical NuvraSchema - present from v2 onwards.
   * When populated, bridge uses this directly rather than reconstructing from
   * flat fields. Absent on v1 payloads and after cloud restores (Supabase table
   * does not yet carry this column - Phase 3 concern).
   */
  canonicalSchema?: NuvraSchema;
}

export interface WriteResult {
  success:      boolean;
  error?:       string;
  sequenceNum?: number;
}

export interface ReadResult {
  found:    boolean;
  payload?: ProjectContentPayload;
  error?:   string;
}

/** Contract for all storage adapters. */
export interface StorageAdapter {
  readonly id:  string;
  write(payload: ProjectContentPayload): Promise<WriteResult>;
  read(projectId: string, userId: string): Promise<ReadResult>;
  isAvailable(): boolean;
}

/* =========================================================
   LOCAL ADAPTER
   Uses persistenceEngine (FNV-1a checksum + StorageEnvelope)
   Write path: sync (Promise.resolve wraps synchronous call)
   Key format: nuvra-project-{projectId} via persistenceEngine
   ========================================================= */

export class LocalAdapter implements StorageAdapter {
  readonly id = "local";

  write(payload: ProjectContentPayload): Promise<WriteResult> {
    const key     = `project-${payload.projectId}`;
    const success = persistSave(key, payload);
    return Promise.resolve({
      success,
      error:       success ? undefined : "localStorage write failed",
      sequenceNum: payload.sequenceNum,
    });
  }

  async read(projectId: string, _userId: string): Promise<ReadResult> {
    const key     = `project-${projectId}`;
    let payload   = persistLoad<ProjectContentPayload>(key);
    if (!payload)  return { found: false };

    // Migrate payload to current schemaVersion if stale
    if (payloadNeedsMigration(payload.schemaVersion)) {
      const fromVersion = payload.schemaVersion ?? 0;
      const result = runMigrations<ProjectContentPayload>({
        scope:       SCOPE_PAYLOAD,
        storageKey:  `nuvra-project-${projectId}`,
        fromVersion,
        toVersion:   CURRENT_PAYLOAD_VERSION,
        data:        payload,
      });
      if (!result.success || !result.data) {
        // Migration failed - do not return corrupted data
        logger.error("LocalAdapter.read: payload migration failed", { projectId, fromVersion });
        return { found: false, error: "Payload migration failed - project data unavailable" };
      }
      payload = result.data;
      // Persist the upgraded payload back to local storage
      persistSave(key, payload);
    }

    return { found: true, payload };
  }

  isAvailable(): boolean {
    if (typeof localStorage === "undefined") return false;
    try {
      const k = "__nuvra_probe__";
      localStorage.setItem(k, "1");
      localStorage.removeItem(k);
      return true;
    } catch {
      return false;
    }
  }
}

/* =========================================================
   SUPABASE ADAPTER
   Writes to project_content table (upsert on project_id+user_id).
   Uses AbortSignal.timeout(15000) for network resilience.
   project_content table must exist before this adapter is used.
   ========================================================= */

export class SupabaseAdapter implements StorageAdapter {
  readonly id = "supabase";

  /**
   * Write project content to Supabase.
   *
   * FAILURE CLASSIFICATION: degrade-and-queue
   *   Local persistence (LocalAdapter) is the primary immediate store.
   *   Supabase is the async cloud-sync layer. A write failure means the sync
   *   is deferred - local data is already committed and safe.
   *
   *   On circuit open: returns { success: false, error: "..." } - does NOT throw.
   *   Callers (persistenceBridge) handle this as a deferred sync, not a fatal error.
   *
   *   Governance: PGRST116 is a valid empty result - not a circuit failure.
   *   Only network errors, auth failures, and server errors (5xx) count.
   */
  async write(payload: ProjectContentPayload): Promise<WriteResult> {
    return supabaseCircuit.execute<WriteResult>(
      async () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from("project_content")
          .upsert(
            {
              project_id:      payload.projectId,
              user_id:         payload.userId,
              pages:           payload.pages,
              blocks:          payload.blocks,
              schema:          payload.schema,
              workflows:       payload.workflows,
              design_system:   payload.designSystem,
              settings:        payload.settings,
              files:           payload.files,
              sequence_num:    payload.sequenceNum,
              schema_version:  payload.schemaVersion,
              saved_at:        new Date(payload.savedAt).toISOString(),
              // Phase 2.5: persist full canonical schema alongside flat projection
              canonical_schema: payload.canonicalSchema ?? null,
            },
            { onConflict: "project_id,user_id" }
          );

        if (error) {
          // Throw so the circuit counts this as a failure.
          // Supabase upsert errors represent real service-level failures.
          logger.warn("SupabaseAdapter.write: Supabase returned error", {
            projectId: payload.projectId,
            error: error.message,
          });
          throw new Error(error.message);
        }

        return { success: true, sequenceNum: payload.sequenceNum };
      },
      {
        // Degrade-and-queue: on circuit open, return honest failure without throwing.
        // The local adapter has already committed the write; this sync is deferred.
        fallback: (): WriteResult => {
          logger.warn("SupabaseAdapter.write: circuit open - cloud sync deferred", {
            projectId: payload.projectId,
          });
          return {
            success: false,
            error: "supabase unavailable - circuit open, local write already committed",
          };
        },
      },
    ).catch((err) => {
      // Non-CircuitOpenError exceptions (network errors that slipped through, etc.)
      const message = err instanceof Error ? err.message : String(err);
      const isClosed = err instanceof CircuitOpenError ? false : true;
      logger.error("SupabaseAdapter.write threw", {
        projectId: payload.projectId,
        error: message,
        circuitOpen: !isClosed,
      });
      return { success: false, error: message };
    });
  }

  /**
   * Read project content from Supabase.
   *
   * FAILURE CLASSIFICATION: local-fallback
   *   On circuit open: returns { found: false } - does NOT throw.
   *   Callers transparently fall back to LocalAdapter.read().
   *
   *   PGRST116 (no rows found) is NOT a circuit failure - it is a valid
   *   empty-result response. Only real service errors count as failures.
   */
  async read(projectId: string, userId: string): Promise<ReadResult> {
    return supabaseCircuit.execute(
      async () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from("project_content")
          .select("*")
          .eq("project_id", projectId)
          .eq("user_id", userId)
          .single();

        if (error) {
          // PGRST116 = no rows found - not an error condition, do NOT count as failure
          if (error.code === "PGRST116") return { found: false };
          // All other errors are real service failures - throw to count against circuit
          throw new Error(error.message);
        }

        if (!data) return { found: false };

        const p: ProjectContentPayload = {
          projectId:      data.project_id,
          userId:         data.user_id,
          pages:          data.pages         ?? [],
          blocks:         data.blocks        ?? [],
          schema:         data.schema        ?? [],
          workflows:      data.workflows     ?? [],
          designSystem:   data.design_system ?? {},
          settings:       data.settings      ?? {},
          files:          data.files         ?? [],
          sequenceNum:    data.sequence_num  ?? 0,
          schemaVersion:  data.schema_version ?? 1,
          savedAt:        new Date(data.saved_at).getTime(),
          // Phase 2.5: canonical schema restored from JSONB column when present
          canonicalSchema: data.canonical_schema ?? undefined,
        };

        return { found: true, payload: p };
      },
      {
        // Local-fallback: on circuit open, return honest empty result without throwing.
        // Callers fall back to LocalAdapter transparently.
        fallback: () => {
          logger.warn("SupabaseAdapter.read: circuit open - using local data only", {
            projectId,
          });
          return {
            found: false,
            error: "supabase unavailable - circuit open, falling back to local adapter",
          };
        },
      },
    ).catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      return { found: false, error: message };
    });
  }

  isAvailable(): boolean {
    return typeof supabase !== "undefined";
  }
}

/** Singleton adapters - lazily constructed. */
export const localAdapter    = new LocalAdapter();
export const supabaseAdapter = new SupabaseAdapter();
